import { FC, useMemo } from 'react';
import type { QuestionWithStatus } from './ExamQuestionsContainer';
import { AnswerIcon } from './AnswerIcon';

interface QuestionDetailProps {
  filteredQuestions: QuestionWithStatus[];
  activeQuestionId: number | null;
}

export const QuestionDetail: FC<QuestionDetailProps> = ({
  filteredQuestions,
  activeQuestionId,
}) => {
  const activeQuestion = useMemo(() => {
    const defaultQuestion = filteredQuestions[0] ?? null;
    if (!activeQuestionId) return defaultQuestion;

    return filteredQuestions.find((q) => q.id === activeQuestionId) ?? defaultQuestion;
  }, [activeQuestionId, filteredQuestions]);

  if (!activeQuestion || !activeQuestion.assessment.answers) return null;

  const objectiveName = activeQuestion.assessment.objective?.name || '';
  const questionText = activeQuestion.assessment.question;
  const answers = activeQuestion.assessment.answers;
  const userAnswerId = activeQuestion.userAnswerId;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p2)]! [font-family:var(--font-montserrat)]! font-medium! underline text-(--neutral-black-NB1000)! m-0!">
          Objective: {objectiveName}
        </h3>
        <p className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! font-medium leading-[150%] text-(--neutral-black-NB700)! m-0!">
          <span className="font-semibold">Question:</span> {questionText}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {answers.map((answer) => {
          const isUserAnswer = answer.id === userAnswerId;
          const showAsCorrect = answer.correct === true;
          const showAsIncorrect = isUserAnswer && answer.correct !== true;

          let bgColor: string;
          let textColor: string;
          let iconType: 'correct' | 'incorrect' | 'neutral';

          switch (true) {
            case showAsCorrect:
              bgColor = 'bg-(--success-S200)!';
              textColor = 'text-(--success-S600)';
              iconType = 'correct';
              break;
            case showAsIncorrect:
              bgColor = 'bg-(--danger-D200)!';
              textColor = 'text-(--danger-D600)';
              iconType = 'incorrect';
              break;
            default:
              bgColor = 'bg-(--neutral-white-NW1000)';
              textColor = 'text-(--neutral-black-NB700)';
              iconType = 'neutral';
          }

          return (
            <div key={answer.id} className={`flex items-center gap-2 rounded-lg p-3 ${bgColor}`}>
              <AnswerIcon type={iconType} />
              <p className={`[font-size:var(--fs-l5)]! ${textColor} m-0! flex-1`}>{answer.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
